<div class="main-view space-y-2 md:space-y-4">
  <app-page-surface-header
    titleIcon="link"
    title="PD Cost Analysis"
    subtitle="What one PD score cost in AI — every model call behind it, and what each one was charged at."
  />

  <div class="panel">
    <div class="flex flex-wrap gap-3">
      <input
        type="text"
        class="form-input flex-1 md:max-w-md"
        placeholder="PD ID or session code (e.g. PD-000001)"
        [(ngModel)]="sessionCode"
        (keyup.enter)="search()"
      />
      <button type="button" [class]="btn.primary" (click)="search()" [disabled]="loading || !sessionCode.trim()">
        {{ loading ? 'Searching…' : 'Search' }}
      </button>
    </div>

    @if (error) {
    <div class="mt-4 rounded border border-warning/40 bg-warning-light p-3 text-warning">
      {{ error }}
      <div class="mt-1 text-xs">
        No record is not the same as no cost — if this PD ran before AI cost tracking was switched on,
        its calls were never recorded.
      </div>
    </div>
    }
  </div>

  @if (!result) {
  <div class="panel">
    <h5 class="mb-3 text-lg font-semibold">Most expensive PDs</h5>
    @if (listLoading) {
    <div class="py-8 text-center text-white-dark">Loading…</div>
    } @else {
    <div class="table-responsive">
      <table class="table-hover">
        <thead>
          <tr><th>PD ID</th><th>Session</th><th>Tenant</th><th>Agent</th><th class="text-right">Calls</th><th class="text-right">Tokens</th><th class="text-right">Cost</th><th>Last call</th></tr>
        </thead>
        <tbody>
          @for (row of pdRows; track row.session_code) {
          <tr>
            <td>
              @if (row.pd_id) {
              <button type="button" class="font-medium text-primary hover:underline" (click)="openPd(row.pd_id!)">
                {{ row.pd_id }}
              </button>
              } @else {
              <span class="text-white-dark">—</span>
              }
            </td>
            <td>
              <button type="button" class="text-primary hover:underline" (click)="openPd(row.session_code)">
                {{ row.session_code }}
              </button>
            </td>
            <td>{{ row.tenant_name }}</td>
            <td>{{ row.agent_id ?? '—' }}</td>
            <td class="text-right">{{ row.calls | number }}</td>
            <td class="text-right">{{ row.total_tokens | number }}</td>
            <td class="text-right font-semibold">{{ formatUsd(row.total_cost_usd) }}</td>
            <td class="whitespace-nowrap text-xs text-white-dark">{{ row.last_at | date : 'dd MMM yy HH:mm' }}</td>
          </tr>
          } @empty {
          <tr><td colspan="8" class="py-8 text-center text-white-dark">No PD has recorded an LLM call yet.</td></tr>
          }
        </tbody>
      </table>
    </div>
    @if (pdMeta.pages > 1) {
    <div class="mt-4 flex items-center justify-between">
      <button type="button" [class]="btn.smallSecondary" [disabled]="pdMeta.page <= 1" (click)="loadList(pdMeta.page - 1)">Previous</button>
      <span class="text-sm text-white-dark">Page {{ pdMeta.page }} of {{ pdMeta.pages }}</span>
      <button type="button" [class]="btn.smallSecondary" [disabled]="pdMeta.page >= pdMeta.pages" (click)="loadList(pdMeta.page + 1)">Next</button>
    </div>
    }
    }
  </div>
  }

  @if (result) {
  <div class="panel">
    <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h5 class="text-lg font-semibold">{{ result.pd.pd_id || result.pd.session_code }}</h5>
        <div class="mt-1 text-sm text-white-dark">
          @if (result.pd.pd_id) { Session: {{ result.pd.session_code }} · }
          @if (result.pd.tenant_code) { Tenant: {{ result.pd.tenant_code }} · } @if (result.pd.agent_id) { Agent
          {{ result.pd.agent_id }} · } {{ result.summary.calls }} LLM calls
        </div>
      </div>
      <button type="button" [class]="btn.smallSecondary" (click)="exportCsv()">Export CSV</button>
    </div>

    <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
      <div class="panel bg-primary-light">
        <div class="text-xs uppercase text-white-dark">Total AI Cost</div>
        <div class="mt-1 text-2xl font-semibold">{{ formatUsd(result.summary.total_cost_usd) }}</div>
      </div>
      <div class="panel">
        <div class="text-xs uppercase text-white-dark">Input Tokens</div>
        <div class="mt-1 text-2xl font-semibold">{{ result.summary.input_tokens | number }}</div>
      </div>
      <div class="panel">
        <div class="text-xs uppercase text-white-dark">Output Tokens</div>
        <div class="mt-1 text-2xl font-semibold">{{ result.summary.output_tokens | number }}</div>
      </div>
      <div class="panel">
        <div class="text-xs uppercase text-white-dark">Total Tokens</div>
        <div class="mt-1 text-2xl font-semibold">{{ result.summary.total_tokens | number }}</div>
      </div>
    </div>
  </div>

  <div class="panel">
    <h5 class="mb-3 text-lg font-semibold">Calls, in the order they ran</h5>
    <div class="table-responsive">
      <table class="table-hover">
        <thead>
          <tr>
            <th>Time</th><th>Task</th><th>Model</th>
            <th class="text-right">Input</th><th class="text-right">Output</th><th class="text-right">Total</th>
            <th class="text-right">Cost</th><th class="text-right">ms</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          @for (call of result.calls; track call.id) {
          <tr>
            <td class="whitespace-nowrap">{{ call.request_timestamp | date : 'HH:mm:ss' }}</td>
            <td>
              <button type="button" class="text-primary hover:underline" title="Open call details"
                (click)="openCall(call.id)">
                {{ call.task_code }}
              </button>
            </td>
            <td>{{ call.model_display_name || call.model_name }}</td>
            <td class="text-right">{{ call.input_tokens ?? '—' }}</td>
            <td class="text-right">{{ call.output_tokens ?? '—' }}</td>
            <td class="text-right">{{ call.total_tokens ?? '—' }}</td>
            <td class="text-right font-semibold">
              {{ formatUsd(call.total_cost_usd) }}
              @if (!call.cost_is_priced) {
              <span class="ml-1 badge bg-warning-light text-warning">unpriced</span>
              }
            </td>
            <td class="text-right">{{ call.processing_time_ms ?? '—' }}</td>
            <td><span [class]="statusClass(call.status)">{{ call.status }}</span></td>
          </tr>
          }
        </tbody>
        <tfoot>
          <tr class="font-semibold">
            <td colspan="3">Total</td>
            <td class="text-right">{{ result.summary.input_tokens | number }}</td>
            <td class="text-right">{{ result.summary.output_tokens | number }}</td>
            <td class="text-right">{{ result.summary.total_tokens | number }}</td>
            <td class="text-right">{{ formatUsd(result.summary.total_cost_usd) }}</td>
            <td colspan="2"></td>
          </tr>
        </tfoot>
      </table>
    </div>
    <a
      [routerLink]="['/llm-usage']"
      [queryParams]="{ session_code: result.pd.session_code }"
      class="mt-3 inline-block text-sm text-primary hover:underline"
    >
      Open these calls in the usage table
    </a>
  </div>
  }

  <app-call-detail-drawer [callId]="openCallId" (closed)="closeCall()" />
</div>
